/**
 * useRecommendations Hook
 * Provides access to collaborative filtering recommendations
 * 
 * Phase 2: Collaborative Filtering frontend integration
 */

import { useState, useEffect, useCallback, useRef } from "react";
import api from "../utils/api/axiosInstance";
import { useUserAuth } from "../contexts/UserAuthContext";

/**
 * Hook to get personalized recommendations
 * @param {Object} options - Options for recommendations
 * @returns {Object} Recommendations data and utilities
 */
export const usePersonalizedRecommendations = (options = {}) => {
  const { categoryId, limit = 10, autoFetch = true } = options;
  const { user } = useUserAuth();
  
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState(null);
  const [isCached, setIsCached] = useState(false);

  const fetchRecommendations = useCallback(async (skipCache = false) => {
    if (!user) {
      setRecommendations([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { limit };
      if (categoryId) params.categoryId = categoryId;
      if (skipCache) params.skipCache = true;

      const res = await api.get("/recommendations", { params });
      
      setRecommendations(res.data.recommendations || []);
      setMethod(res.data.method);
      setIsCached(res.data.cached);
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.response?.data?.message || "Failed to load recommendations");
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [user, categoryId, limit]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && user) {
      fetchRecommendations();
    }
  }, [autoFetch, user, fetchRecommendations]);

  const refresh = useCallback(() => {
    fetchRecommendations(true);
  }, [fetchRecommendations]);

  return {
    recommendations,
    loading,
    error,
    method,
    isCached,
    refetch: fetchRecommendations,
    refresh,
  };
};

/**
 * Hook to get related products for a specific product
 * @param {string} productId - Product ID
 * @param {Object} options - Options
 * @returns {Object} Related products data
 */
export const useRelatedProducts = (productId, options = {}) => {
  const { limit = 10, autoFetch = true } = options;
  
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState(null);

  const fetchRelatedProducts = useCallback(async (skipCache = false) => {
    if (!productId) {
      setRelatedProducts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { limit };
      if (skipCache) params.skipCache = true;

      const res = await api.get(`/recommendations/related/${productId}`, { params });
      
      setRelatedProducts(res.data.relatedProducts || []);
      setMethod(res.data.method);
    } catch (err) {
      console.error("Error fetching related products:", err);
      setError(err.response?.data?.message || "Failed to load related products");
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [productId, limit]);

  useEffect(() => {
    if (autoFetch && productId) {
      fetchRelatedProducts();
    }
  }, [autoFetch, productId, fetchRelatedProducts]);

  return {
    relatedProducts,
    loading,
    error,
    method,
    refetch: fetchRelatedProducts,
  };
};

/**
 * Hook to discover users to follow
 */
export const useDiscoverUsers = (options = {}) => {
  const { limit = 20, autoFetch = true } = options;
  const { user } = useUserAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDiscoverUsers = useCallback(async () => {
    if (!user) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/users/discover", { params: { limit } });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error("Error fetching discover users:", err);
      setError(err.response?.data?.message || "Failed to load suggested users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [user, limit]);

  useEffect(() => {
    if (autoFetch && user) {
      fetchDiscoverUsers();
    }
  }, [autoFetch, user, fetchDiscoverUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchDiscoverUsers,
  };
};

/**
 * Hook to track user interactions with products
 * This feeds the collaborative filtering algorithm
 * @returns {Object} Interaction tracking utilities
 */
export const useInteractionTracker = () => {
  const { user } = useUserAuth();
  const interactionQueue = useRef([]);
  const flushTimeoutRef = useRef(null);

  /**
   * Record a single interaction
   */
  const recordInteraction = useCallback(async (productId, interactionType, data = {}) => {
    if (!user || !productId) return;

    try {
      await api.post("/recommendations/interactions", {
        productId,
        interactionType,
        data: { surface: "recommendations", ...data },
      });
    } catch (err) {
      console.error("Error recording interaction:", err);
    }
  }, [user]);

  /**
   * Queue an interaction for batch processing
   * Useful for high-frequency events like views
   */
  const queueInteraction = useCallback((productId, interactionType, data = {}) => {
    if (!user || !productId) return;

    interactionQueue.current.push({ productId, interactionType, data });

    // Debounce flush
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }

    flushTimeoutRef.current = setTimeout(() => {
      flushInteractionQueue();
    }, 2000); // Flush after 2 seconds of inactivity
  }, [user]);

  /**
   * Flush the interaction queue
   */
  const flushInteractionQueue = useCallback(async () => {
    if (!user || interactionQueue.current.length === 0) return;

    const interactions = [...interactionQueue.current];
    interactionQueue.current = [];

    try {
      await api.post("/recommendations/interactions/batch", { interactions });
    } catch (err) {
      console.error("Error flushing interaction queue:", err);
    }
  }, [user]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      if (interactionQueue.current.length > 0) {
        flushInteractionQueue();
      }
    };
  }, [flushInteractionQueue]);

  // Convenience methods for common interactions
  const trackView = useCallback((productId, duration = 0) => {
    queueInteraction(productId, "view", { duration });
  }, [queueInteraction]);

  const trackClick = useCallback((productId, source = "unknown") => {
    queueInteraction(productId, "click", { source });
  }, [queueInteraction]);

  const trackFavorite = useCallback((productId, isFavorite) => {
    recordInteraction(productId, "favorite", { status: isFavorite });
  }, [recordInteraction]);

  const trackCompare = useCallback((productId) => {
    recordInteraction(productId, "compare");
  }, [recordInteraction]);

  const trackList = useCallback((productId) => {
    recordInteraction(productId, "list");
  }, [recordInteraction]);

  const trackReview = useCallback((productId, rating) => {
    recordInteraction(productId, "review", { rating });
  }, [recordInteraction]);

  const trackWizardSelect = useCallback((productId, sessionId) => {
    recordInteraction(productId, "wizardSelect", { sessionId });
  }, [recordInteraction]);

  const trackShare = useCallback((productId) => {
    recordInteraction(productId, "share");
  }, [recordInteraction]);

  return {
    recordInteraction,
    queueInteraction,
    flushInteractionQueue,
    trackView,
    trackClick,
    trackFavorite,
    trackCompare,
    trackList,
    trackReview,
    trackWizardSelect,
    trackShare,
  };
};

/**
 * Hook to get user's interaction history
 * @param {Object} options - Options
 * @returns {Object} Interaction history data
 */
export const useInteractionHistory = (options = {}) => {
  const { limit = 50, minEngagement = 0, categoryId, autoFetch = true } = options;
  const { user } = useUserAuth();
  
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!user) {
      setInteractions([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { limit, minEngagement };
      if (categoryId) params.categoryId = categoryId;

      const res = await api.get("/recommendations/interactions/history", { params });
      
      setInteractions(res.data.interactions || []);
      setStats(res.data.stats);
    } catch (err) {
      console.error("Error fetching interaction history:", err);
      setError(err.response?.data?.message || "Failed to load history");
      setInteractions([]);
    } finally {
      setLoading(false);
    }
  }, [user, limit, minEngagement, categoryId]);

  useEffect(() => {
    if (autoFetch && user) {
      fetchHistory();
    }
  }, [autoFetch, user, fetchHistory]);

  return {
    interactions,
    stats,
    loading,
    error,
    refetch: fetchHistory,
  };
};

/**
 * Hook to get "Users like you chose" recommendations (Phase 7)
 * @param {Object} options - Options
 * @returns {Object} Recommendations data
 */
export const useUsersLikeYouChose = (options = {}) => {
  const { limit = 5, categoryId, autoFetch = true } = options;
  const { user } = useUserAuth();

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState(null);
  const [similarUsersCount, setSimilarUsersCount] = useState(0);

  const fetchRecommendations = useCallback(async (skipCache = false) => {
    if (!user) {
      setRecommendations([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { limit };
      if (categoryId) params.categoryId = categoryId;
      if (skipCache) params.skipCache = true;

      const res = await api.get("/recommendations/users-like-you", { params });

      setRecommendations(res.data.recommendations || []);
      setMethod(res.data.method);
      setSimilarUsersCount(res.data.similarUsersCount || 0);
    } catch (err) {
      console.error("Error fetching users-like-you recommendations:", err);
      setError(err.response?.data?.message || "Failed to load recommendations");
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [user, limit, categoryId]);

  useEffect(() => {
    if (autoFetch && user) {
      fetchRecommendations();
    }
  }, [autoFetch, user, fetchRecommendations]);

  return {
    recommendations,
    loading,
    error,
    method,
    similarUsersCount,
    refetch: fetchRecommendations,
  };
};

/**
 * Hook to get collaborative filtering boost for a product (Phase 7)
 * @param {string} productId - Product ID
 * @param {Object} options - Options
 * @returns {Object} Boost data
 */
export const useCollaborativeBoost = (productId, options = {}) => {
  const { autoFetch = true } = options;
  const { user } = useUserAuth();

  const [boost, setBoost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBoost = useCallback(async (skipCache = false) => {
    if (!user || !productId) {
      setBoost(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (skipCache) params.skipCache = true;

      const res = await api.get(`/recommendations/boost/${productId}`, { params });
      setBoost(res.data);
    } catch (err) {
      console.error("Error fetching collaborative boost:", err);
      setError(err.response?.data?.message || "Failed to load boost");
      setBoost(null);
    } finally {
      setLoading(false);
    }
  }, [user, productId]);

  useEffect(() => {
    if (autoFetch && user && productId) {
      fetchBoost();
    }
  }, [autoFetch, user, productId, fetchBoost]);

  return {
    boost,
    loading,
    error,
    refetch: fetchBoost,
  };
};

/**
 * Hook to get "For You" feed recommendations (Phase 7)
 * @param {Object} options - Options
 * @returns {Object} Feed data
 */
export const useForYouFeed = (options = {}) => {
  const { limit = 20, categoryId, autoFetch = true } = options;
  const { user } = useUserAuth();

  const [feed, setFeed] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFeed = useCallback(async (skipCache = false) => {
    if (!user) {
      setFeed([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { limit };
      if (categoryId) params.categoryId = categoryId;
      if (skipCache) params.skipCache = true;

      const res = await api.get("/recommendations/for-you", { params });

      setFeed(res.data.feed || []);
      setMeta(res.data.meta || null);
    } catch (err) {
      console.error("Error fetching for you feed:", err);
      setError(err.response?.data?.message || "Failed to load feed");
      setFeed([]);
    } finally {
      setLoading(false);
    }
  }, [user, limit, categoryId]);

  useEffect(() => {
    if (autoFetch && user) {
      fetchFeed();
    }
  }, [autoFetch, user, fetchFeed]);

  return {
    feed,
    meta,
    loading,
    error,
    refetch: fetchFeed,
  };
};

// Default export with all hooks
export default {
  usePersonalizedRecommendations,
  useRelatedProducts,
  useDiscoverUsers,
  useInteractionTracker,
  useInteractionHistory,
  useUsersLikeYouChose,
  useCollaborativeBoost,
  useForYouFeed,
};
